/**
 * FolderSettingsDialog — a folder's settings: its name, its classification and
 * the departments that may see it. Reached from the folder menu in KbSidebar.
 *
 * Library LB6-T8, acceptance L6-08. Decision 31 (B10): a folder's departments
 * are set in folder settings, with the Users rail's department-scope picker —
 * never in the Share dialog. Decision 23 (B4): a document in the folder with no
 * departments of its own follows them; that is decided once, in the api's
 * Document visibility resolver.
 *
 * EACH FIELD ASKS THE KEY ITS ROUTE DECLARES (decision 26 / B1):
 *   Name, Classification → write:library.documents (PUT /wiki/folders/:id)
 *   Visible to departments → share:library (PUT /library/acl/folders/:id/departments, OQ-L6-2)
 * A field the person may not use is not drawn, and nothing is sent for it.
 *
 * CLASSIFICATION FOLLOWS THE FRAMEWORKS (decision 28 / B7 + decision 37, the
 * LB6-T7 rule): sensitivity and export control are shown — and sent — only
 * while a compliance framework that shows them is active. A hidden control
 * sends no key; the folder keeps the classification it has, and the api still
 * enforces it.
 *
 * The folder is read from THE folder list (lib/use-library-folders.ts) — the
 * same list the sidebar draws; a folder that list cannot answer for is not
 * edited here.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { useHasPermission } from '@/lib/use-has-permission';
import { invalidateLibraryFolders, useLibraryFolders } from '@/lib/use-library-folders';
import {
  HIDDEN_CLASSIFICATION_CONTROLS,
  classificationFields,
  invalidateClassificationPolicy,
  useClassificationPolicy,
  type ClassificationControlsView,
} from '@/lib/use-classification-policy';
import { FolderListError } from '@/components/library/folder-picker-states';
import { ItemDepartmentsField, useItemDepartmentsDraft } from '@/components/library/item-departments-field';
import { ExportControlSelect, SensitivitySelect } from '@/components/library/classification-selects';

/**
 * The body of PUT /wiki/folders/:id: only what changed, and a classification
 * key only for a control the tenant's frameworks show (a hidden control sends
 * nothing — never its stored value, never a default). Empty when there is
 * nothing to write.
 */
export function folderSettingsBody(
  folder: { name: string; sensitivity: string; exportControl: string },
  edit: { name: string | null; sensitivity: string | null; exportControl: string | null },
  controls: ClassificationControlsView,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (edit.name !== null && edit.name.trim() !== '' && edit.name.trim() !== folder.name) {
    body.name = edit.name.trim();
  }
  const shown = classificationFields(controls, {
    sensitivity: edit.sensitivity ?? folder.sensitivity,
    exportControl: edit.exportControl ?? folder.exportControl,
  });
  if (shown.sensitivity !== undefined && shown.sensitivity !== folder.sensitivity) body.sensitivity = shown.sensitivity;
  if (shown.exportControl !== undefined && shown.exportControl !== folder.exportControl) body.exportControl = shown.exportControl;
  return body;
}

interface FolderSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The folder whose settings are open; null when closed. */
  folderId: string | null;
}

export function FolderSettingsDialog({ open, onOpenChange, folderId }: FolderSettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Folder settings</DialogTitle>
          <DialogDescription className="text-xs">
            The folder’s name, its classification and the departments that may see what is in it.
          </DialogDescription>
        </DialogHeader>
        {open && folderId ? (
          <FolderSettingsBody key={folderId} folderId={folderId} onClose={() => onOpenChange(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** The dialog's fields and its Save. Exported for the per-permission test. */
export function FolderSettingsBody({ folderId, onClose }: { folderId: string; onClose: () => void }) {
  const canWriteFolder = useHasPermission('write', 'library.documents');
  const queryClient = useQueryClient();
  const foldersQ = useLibraryFolders();
  const folder = foldersQ.data?.find((f) => f.id === folderId) ?? null;
  const policy = useClassificationPolicy({ enabled: canWriteFolder });
  const controls = canWriteFolder ? policy.controls : HIDDEN_CLASSIFICATION_CONTROLS;
  const departments = useItemDepartmentsDraft({ kind: 'folder', id: folderId });

  // null = untouched: the field shows (and the save compares with) the stored value.
  const [name, setName] = useState<string | null>(null);
  const [sensitivity, setSensitivity] = useState<string | null>(null);
  const [exportControl, setExportControl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!folder) {
    return (
      <div className="py-2">
        {foldersQ.isError ? (
          <FolderListError consequence="The folder’s settings cannot be changed until it loads." onRetry={() => void foldersQ.refetch()} />
        ) : foldersQ.isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />Loading the folder…
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">This folder is no longer in the Library.</p>
        )}
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </div>
    );
  }

  const folderBody = canWriteFolder
    ? folderSettingsBody(folder, { name, sensitivity, exportControl }, controls)
    : {};
  const nameInvalid = canWriteFolder && name !== null && name.trim() === '';
  const changed = Object.keys(folderBody).length > 0 || departments.dirty;
  const canSave = changed && !saving && !nameInvalid && departments.complete;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    if (Object.keys(folderBody).length > 0) {
      try {
        await apiClient.put(`/wiki/folders/${folder.id}`, folderBody);
      } catch (err) {
        // The server's reason — 403 INSUFFICIENT_CLEARANCE above the editor's
        // own clearance (B6), 400 FRAMEWORK_NOT_ACTIVE when a framework was
        // turned off since the dialog opened (re-asked below), a name clash.
        toast.error(getApiErrorMessage(err, 'Failed to save the folder'));
        void invalidateClassificationPolicy(queryClient);
        setSaving(false);
        return;
      }
    }
    try {
      await departments.save(folder.id);
    } catch (err) {
      toast.error(`Folder saved, but its departments were not changed: ${getApiErrorMessage(err, 'the request failed')}`);
      void invalidateLibraryFolders(queryClient);
      setSaving(false);
      return;
    }
    // Every folder reader (sidebar, pickers, the detail page) sees the change.
    void invalidateLibraryFolders(queryClient);
    toast.success('Folder settings saved');
    setSaving(false);
    onClose();
  };

  return (
    <>
      <div className="space-y-4 py-2">
        {canWriteFolder ? (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium" htmlFor="folder-settings-name">Folder Name</Label>
            <Input
              id="folder-settings-name"
              value={name ?? folder.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
        ) : null}

        {/* Classification — only the controls a framework shows (LB6-T7 rule) */}
        {canWriteFolder && controls.any ? (
          <div className="rounded-md border border-muted p-3 space-y-3" data-testid="folder-settings-classification">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Classification</p>
            <div className="grid grid-cols-2 gap-3">
              {controls.sensitivity ? (
                <SensitivitySelect value={sensitivity ?? folder.sensitivity} onChange={setSensitivity} />
              ) : null}
              {controls.exportControl ? (
                <ExportControlSelect value={exportControl ?? folder.exportControl} onChange={setExportControl} />
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Visible to departments — share:library only (OQ-L6-2) */}
        <ItemDepartmentsField draft={departments} disabled={saving} />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={!canSave}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </>
  );
}
